// ═══════════════════════════════════════════════════════════
// 视频详情 — 获取详情 + 渲染剧集网格
// ═══════════════════════════════════════════════════════════

import { TIMEOUT } from '../config/constants.js';
import { esc, renderEpisodeButtons } from '../lib/utils.js';
import { callApi } from '../lib/http.js';
import { showLoading, hideLoading } from '../ui/loading.js';
import { showToast } from '../ui/toast.js';
import { openModal } from '../ui/modal.js';
import { getSiteName } from '../config/sites.js';

// 模块状态
let currentEpisodes = [];
let currentEpisodeIndex = 0;
let episodesReversed = false;
let currentVideoTitle = '';
let currentVideoInfo = null;

export function getPlayerState() {
    return {
        episodes: currentEpisodes,
        episodeIndex: currentEpisodeIndex,
        reversed: episodesReversed,
        title: currentVideoTitle,
        info: currentVideoInfo,
    };
}

export async function showDetails(id, vodName) {
    if (!id) {
        showToast('视频ID无效', 'error');
        return;
    }

    showLoading();
    const source = window.getCurrentSource ? window.getCurrentSource() : 'hnzy';
    const customUrl = window.getCustomApiUrl ? window.getCustomApiUrl() : '';

    const params = { id };
    if (source === 'custom') {
        params.customApi = customUrl;
    } else {
        params.source = source;
    }

    try {
        const data = await callApi('api/detail', params, { timeout: TIMEOUT.DETAIL });

        currentVideoTitle = vodName || '未知视频';
        currentEpisodes = [];
        currentEpisodeIndex = 0;
        episodesReversed = false;

        const info = data.videoInfo || {};

        currentVideoInfo = {
            vod_id: id,
            title: vodName || info.title || '未知视频',
            poster: info.cover || '',
            type: info.type || '',
            year: info.year || '',
            area: info.area || '',
            source: source,
        };

        const infoHTML = info.cover ? `
            <div class="video-summary">
                <img src="${esc(info.cover)}" alt="" loading="lazy"
                     class="thumb"
                     onerror="this.style.display='none'" />
                <div class="meta">
                    ${info.year ? `<span>📅 ${esc(info.year)}${info.area ? ' · ' + esc(info.area) : ''}</span>` : ''}
                    ${info.type ? `<span>📂 ${esc(info.type)}</span>` : ''}
                    ${info.director ? `<span>🎬 ${esc(info.director)}</span>` : ''}
                    ${info.remarks ? `<span>💿 ${esc(info.remarks)}</span>` : ''}
                    ${info.desc ? `<p style="font-size:0.78rem;opacity:0.6;margin-top:4px">${esc(info.desc).replace(/<[^>]*>/g, '').substring(0, 120)}</p>` : ''}
                </div>
            </div>` : '';

        if (data.episodes && data.episodes.length > 0) {
            const safeEpisodes = data.episodes
                .map(url => (url && (url.startsWith('http://') || url.startsWith('https://')) ? url.replace(/"/g, '&quot;') : ''))
                .filter(url => url);

            currentEpisodes = safeEpisodes;

            const episodeGrid = renderEpisodeButtons(currentEpisodes, currentEpisodeIndex, episodesReversed);

            const modalHTML = `
                ${infoHTML}
                <div class="episode-toolbar">
                    <button id="toggleOrderBtn" class="episode-sort-btn">
                        <svg xmlns="http://www.w3.org/2000/svg" style="width:16px;height:16px" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clip-rule="evenodd" />
                        </svg>
                        <span>倒序排列</span>
                    </button>
                </div>
                <div id="episodesGrid" class="episode-grid">
                    ${episodeGrid}
                </div>
            `;

            openModal(currentVideoTitle, modalHTML);

            // 绑定排序按钮
            setTimeout(() => {
                const btn = document.getElementById('toggleOrderBtn');
                if (btn) btn.addEventListener('click', toggleEpisodeOrder);

                // 绑定剧集按钮
                document.querySelectorAll('#episodesGrid .episode-btn').forEach(btn => {
                    btn.addEventListener('click', () => {
                        const idx = parseInt(btn.dataset.index);
                        const url = btn.dataset.url;
                        playEpisode(url, idx);
                    });
                });
            }, 0);

        } else {
            openModal(currentVideoTitle, infoHTML + '<p class="text-center text-gray-400 py-8">没有找到可播放的视频</p>');
        }

    } catch (error) {
        console.error('获取详情错误:', error);
        showToast(error.message || '获取详情失败，请稍后重试', 'error');
    } finally {
        hideLoading();
    }
}

function playEpisode(url, episodeIndex) {
    if (!url) {
        showToast('无效的视频链接', 'error');
        return;
    }

    currentEpisodeIndex = episodeIndex;

    // 保存播放状态
    if (typeof window.savePlayerStateAndNavigate === 'function') {
        window.savePlayerStateAndNavigate(url, currentVideoTitle, episodeIndex);
    } else {
        // 降级：直接存储 localStorage
        localStorage.setItem('libretv_player_state', JSON.stringify({
            title: currentVideoTitle,
            episodeIndex: episodeIndex,
            episodes: currentEpisodes,
            reversed: episodesReversed,
        }));

        const playerUrl = `player.html?url=${encodeURIComponent(url)}&title=${encodeURIComponent(currentVideoTitle)}&index=${episodeIndex}`;
        window.open(playerUrl, '_blank');
    }

    // 保存观看历史
    if (typeof window.saveWatchHistoryEntry === 'function') {
        window.saveWatchHistoryEntry(currentVideoTitle, episodeIndex);
    }
}

function toggleEpisodeOrder() {
    episodesReversed = !episodesReversed;
    const grid = document.getElementById('episodesGrid');
    if (grid) {
        grid.innerHTML = renderEpisodeButtons(currentEpisodes, currentEpisodeIndex, episodesReversed);
        bindEpisodeClicks();
    }

    const btn = document.getElementById('toggleOrderBtn');
    if (btn) {
        btn.querySelector('span').textContent = episodesReversed ? '正序排列' : '倒序排列';
        const icon = btn.querySelector('svg');
        if (icon) icon.style.transform = episodesReversed ? 'rotate(180deg)' : 'rotate(0deg)';
    }
}

function bindEpisodeClicks() {
    document.querySelectorAll('#episodesGrid .episode-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const idx = parseInt(btn.dataset.index);
            const url = btn.dataset.url;
            playEpisode(url, idx);
        });
    });
}

// 全局暴露
window.showVideoDetails = showDetails;
window.toggleEpisodeOrder = toggleEpisodeOrder;
window.getCurrentEpisodes = () => currentEpisodes;
window.getCurrentVideoInfo = () => currentVideoInfo;
